import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { AdminGuard } from 'app/core/auth/guards/admin.guard';
import { GuestMatchGuard } from 'app/core/auth/guards/guest-match.guard';
import { AuthenticatedMatchGuard } from 'app/core/auth/guards/authenticated-match.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Landing routes
  {
    path: '',
    component: LayoutComponent,
    data: {
      layout: 'landing',
    },
    children: [
      {
        path: '',
        loadChildren: () => import('app/modules/landing/home/home.module').then((m) => m.LandingHomeModule),
        pathMatch: 'full',
        data: {
          title: 'Закрути рекламу в телеграм',
          showHeader: false,
          backgroundColor: '#fff'
        },
      },
      {
        path: 'regulations',
        loadChildren: () =>
          import('app/modules/landing/regulations/regulations.module').then((m) => m.LandingRegulationsModule),
        pathMatch: 'full',
        data: { title: 'Политика обработки персональных данных'}
      },
      {
        path: 'about-us',
        loadChildren: () =>
          import('app/modules/landing/about-us/about-us.module').then((m) => m.AboutUsModule),
        pathMatch: 'full',
        data: { title: 'О компании'}
      },
      {
        path: 'privacy-policy',
        loadChildren: () =>
          import('app/modules/landing/privacy-policy/privacy-policy.module').then(
            (m) => m.PrivacyPolicyModule
          ),
        pathMatch: 'full',
        data: { title: 'Условия использования'}
      },
      {
        path: 'advertiser-info/:id',
        loadChildren: () =>
          import('app/modules/landing/advertiser-info/advertiser-info.module').then(
            (m) => m.AdvertiserInfoModule
          ),
        pathMatch: 'full',
        data: { title: 'О рекламодателе'}
      },
      {
        path: 'news',
        canMatch: [GuestMatchGuard],
        loadChildren: () => import('app/modules/news/news.module').then((m) => m.NewsModule),
        data: { title: 'Новости и обновления' }
      },
      {
        path: 'collections/join/:token',
        canMatch: [GuestMatchGuard],
        loadChildren: () =>
          import('./modules/main/collections/public-join/public-join.module').then(
            (m) => m.CollectionsPublicJoinModule,
          ),
        data: { title: 'Участие в подборке' },
      },
    ],
  },

  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard/onboarding' },

  // Auth routes for guests
  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'auth',
    },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () =>
          import('app/modules/auth/confirmation-required/confirmation-required.module').then(
            (m) => m.AuthConfirmationRequiredModule,
          ),
      },
      {
        path: 'sign-up',
        loadChildren: () => import('app/modules/auth/sign-up/sign-up.module').then((m) => m.AuthSignUpModule),
        data: {
          title: 'Добро пожаловать!',
          description: `Зарегистрируйтесь, чтобы получить доступ к уникальным возможностям размещения рекламы в Telegram-каналах. Удобный интерфейс, прозрачная аналитика и тысячи проверенных площадок для продвижения ждут вас!`,
        },
      },
      {
        path: 'sign-in',
        loadChildren: () => import('app/modules/auth/sign-in/sign-in.module').then((m) => m.AuthSignInModule),
        data: {
          title: 'Рады снова видеть вас!',
          description: `Войдите в свой аккаунт, чтобы продолжить работу: управлять рекламными кампаниями, находить подходящие каналы и отслеживать результаты продвижения.`,
        },
      },
      {
        path: 'reset-password',
        loadChildren: () =>
          import('app/modules/auth/reset-password/reset-password.module').then((m) => m.AuthResetPasswordModule),
        data: {
          title: 'Забыли пароль? Всё под контролем!',
          description: `Следуйте простым шагам, чтобы восстановить доступ к своему аккаунту. Вернуться к управлению рекламой — быстрее, чем вы думаете!`,
        },
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: 'empty',
    },
    children: [
      {
        path: 'sign-out',
        loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then((m) => m.AuthSignOutModule),
      },
      {
        path: 'unlock-session',
        loadChildren: () =>
          import('app/modules/auth/unlock-session/unlock-session.module').then((m) => m.AuthUnlockSessionModule),
      },
    ],
  },

  // Main routes
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: InitialDataResolver,
    },
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./modules/main/dashboard/dashboard.module').then((m) => m.DashboardModule),
      },
      {
        path: 'advertiser',
        loadChildren: () => import('./modules/main/advertiser/advertiser.module').then((m) => m.AdvertiserModule),
      },
      {
        path: 'performer',
        loadChildren: () => import('./modules/main/performer/performer.module').then((m) => m.PerformerModule),
      },
      {
        path: 'account',
        loadChildren: () => import('app/modules/main/account/account.module').then((m) => m.AccountModule),
      },
      {
        path: 'news',
        canMatch: [AuthenticatedMatchGuard],
        loadChildren: () => import('app/modules/news/news.module').then((m) => m.NewsModule),
      },
      {
        path: 'ideas',
        loadChildren: () => import('app/modules/main/ideas/ideas.module').then((m) => m.IdeasModule),
      },
      {
        path: 'collections/join/:token',
        canMatch: [AuthenticatedMatchGuard],
        loadChildren: () =>
          import('./modules/main/collections/public-join/public-join.module').then(
            (m) => m.CollectionsPublicJoinModule,
          ),
        data: { title: 'Участие в подборке' },
      },
      {
        path: 'collections',
        loadChildren: () => import('app/modules/main/collections/collections.module').then((m) => m.CollectionsModule),
      },
      {
        path: 'admin',
        canActivate: [AdminGuard],
        children: [
          {
            path: 'news',
            loadChildren: () =>
              import('./modules/main/admin/news-management/news-management.module').then(
                (m) => m.NewsManagementModule,
              ),
          },
          {
            path: 'moderation',
            loadChildren: () =>
              import('./modules/main/admin/platform-moderation/platform-moderation.module').then(
                (m) => m.PlatformModerationModule,
              ),
          },
          {
            path: 'payments',
            loadChildren: () =>
              import('./modules/main/admin/payments-administration/payments-administration.module').then(
                (m) => m.PaymentsAdministrationModule,
              ),
          },
          {
            path: 'newsletter',
            loadChildren: () =>
              import('./modules/main/admin/newsletter/newsletter.module').then((m) => m.NewsletterModule),
          },
          {
            path: 'promo-codes',
            loadChildren: () =>
              import('./modules/main/admin/promo-codes-management/promo-codes-management.module').then(
                (m) => m.PromoCodesManagementModule,
              ),
          },
        ],
      },
    ],
  },

  // 404 & Catch all
  {
    path: '404-not-found',
    pathMatch: 'full',
    loadChildren: () =>
      import('app/modules/technical-pages/error/error-404/error-404.module').then((m) => m.Error404Module),
  },
  { path: '**', redirectTo: '404-not-found' },
];
